import { DropdownMenu, Icon, TabLines } from '@toggle/design-system';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router-dom';

import { appPaths } from '~/routes/app-paths';
import { useNewsWatchlistsStore } from '~/stores/use-news-watchlists/useNewsWatchlistsStore';
import { useWatchlist } from '~/stores/use-watchlist/useWatchlist';
import { TabsType } from '~/views/news/utils/utils';

import * as S from './NewsTabs.styles';

const SUFFIX = '...';
const MAX_LIST_NAME_LENGTH = 30;

export interface NewsTabProps {
  activeTab: TabsType;
  onChange: (_: number, tabKey: TabsType) => void;
}

export const NewsTabs = ({ activeTab, onChange }: NewsTabProps) => {
  const { t } = useTranslation(['news']);
  const location = useLocation();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { lists } = useWatchlist();
  const { watchlistsToCheck, initialize, toggleSelectAll, toggleSelectOne } =
    useNewsWatchlistsStore();

  const isNewsPath = !!matchPath({ path: appPaths.news }, location.pathname);

  useEffect(() => {
    initialize(lists);
  }, [initialize, lists]);

  const isAllChecked = watchlistsToCheck.every(list => list.isChecked);
  const isPartialChecked = isAllChecked
    ? false
    : watchlistsToCheck.some(list => list.isChecked);

  const watchlistTabLabel =
    isAllChecked || !isNewsPath
      ? t('news:tabs.allWatchlists')
      : t('news:tabs.tabWatchlistsCount', {
          count: watchlistsToCheck.filter(list => list.isChecked).length,
        });

  const newsTabs = [
    {
      tabKey: TabsType.MostRead,
      tabLabel: t('news:tabs.mostRead'),
      iconName: 'Trending',
    } as const,
    {
      tabKey: TabsType.Watchlist,
      tabLabel: (
        <S.WatchlistLabelWrapper>{watchlistTabLabel}</S.WatchlistLabelWrapper>
      ),
      iconName: 'Watchlist',
      customBottomElement: isNewsPath ? (
        <DropdownMenu
          open={isDropdownOpen}
          onOpenChange={() => setIsDropdownOpen(prev => !prev)}
          triggerNode={
            <S.IconWrapper>
              <Icon
                iconName="ChevronBoldDown"
                size={12}
                fillColor={
                  activeTab === TabsType.Watchlist
                    ? 'var(--icon-primary)'
                    : 'var(--text-default)'
                }
                data-testid="watchlists-dropdown-icon"
              />
            </S.IconWrapper>
          }
          contentProps={{
            asChild: true,
            sticky: 'always',
            alignOffset: -140,
          }}
        >
          <S.StyledDropdownWrapper>
            <S.DropdownCheckbox
              label={t('news:tabs.allWatchlists')}
              name={t('news:tabs.allWatchlists')}
              checked={isPartialChecked || isAllChecked}
              partial={isPartialChecked}
              onChange={() => toggleSelectAll(isAllChecked)}
            />
            {watchlistsToCheck.map(list => (
              <S.DropdownCheckbox
                key={list.id}
                label={
                  list.name.length > MAX_LIST_NAME_LENGTH
                    ? `${list.name.substring(
                        0,
                        MAX_LIST_NAME_LENGTH - SUFFIX.length
                      )}${SUFFIX}`
                    : list.name
                }
                name={list.name}
                checked={list.isChecked}
                onChange={() => toggleSelectOne(list.id)}
                testId={`checkbox-${list.id}`}
              />
            ))}
          </S.StyledDropdownWrapper>
        </DropdownMenu>
      ) : undefined,
    } as const,
  ];

  return <TabLines tabs={newsTabs} activeTab={activeTab} onChange={onChange} />;
};
